"use client";

import React from "react";
import { useRouter } from "next/navigation";
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  Layers,
  MapPin,
  Filter,
  Phone,
  FileText,
  Building2,
  CheckCircle2,
} from "lucide-react";
import { useFpoDetailPage } from "@/modules/public/home/hooks/usePublicData";

export default function FpoConnectDetailPage({
  params,
}: {
  params: Promise<{ slug: string }>;
}) {
  const router = useRouter();
  const unwrappedParams = React.use(params);
  const slug = unwrappedParams.slug;

  const {
    stocks,
    loading,
    error,
    selectedCategory,
    setSelectedCategory,
    filteredStocks,
    uniqueCategories,
  } = useFpoDetailPage(slug);

  const isMongoId = /^[0-9a-fA-F]{24}$/.test(slug);
  React.useEffect(() => {
    if (isMongoId && stocks.length > 0) {
      const fpoUser = stocks[0].fpoUser;
      const actualSlug = typeof fpoUser === "object" ? fpoUser?.slug : null;
      if (actualSlug) {
        router.replace(`/fpo-connect/${actualSlug}`);
      }
    }
  }, [isMongoId, stocks, router]);

  if (loading) {
    return (
      <div className="min-h-[75vh] flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="w-12 h-12 text-green-600 animate-spin mb-4" />
        <p className="text-gray-650 font-bold text-sm">Opening FPO Showroom...</p>
      </div>
    );
  }

  if (error || stocks.length === 0) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center px-4 bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-sm border border-gray-200 text-center max-w-md">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Profile Not Found</h2>
          <p className="text-gray-650 mb-6">
            {error || "The FPO Profile you are looking for does not exist."}
          </p>
          <button
            onClick={() => router.push("/fpo-connect")}
            className="inline-flex items-center gap-2 bg-green-600 text-white font-bold px-6 py-2.5 rounded-md hover:bg-green-700 transition"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Directory
          </button>
        </div>
      </div>
    );
  }

  // Derive FPO profile info from the first stock entry (fpoUser is the same across all)
  const firstStock = stocks[0];
  const fpoUser = firstStock.fpoUser;
  const fpoName = firstStock.fpoName;
  const registeredDistrict = fpoUser?.district || firstStock.district;
  const registeredDistrictName = typeof registeredDistrict === "object" ? registeredDistrict.name : registeredDistrict;
  const registeredTaluk = fpoUser?.taluk || firstStock.taluk;
  const registeredTalukName = typeof registeredTaluk === "object" ? registeredTaluk.name : registeredTaluk;
  const registeredAddress = fpoUser?.address;
  const registeredPhone = fpoUser?.phoneNumber || firstStock.mobileNumber;
  const gstNumber = firstStock.gstNumber || fpoUser?.gstNumber;
  const allActivities = Array.from(
    new Set(stocks.flatMap((s) => s.activities || []))
  ).filter(Boolean);
  const allCategories = Array.from(
    new Set(stocks.map((s) => s.category?.name).filter(Boolean))
  );


  return (
    <div className="bg-gray-50 min-h-screen py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Back Button */}
        <div className="flex items-center">
          <button
            onClick={() => router.push("/fpo-connect")}
            className="inline-flex items-center gap-2 text-xs sm:text-sm text-green-700 hover:text-green-800 font-bold transition cursor-pointer"
          >
            <ArrowLeft className="w-4.5 h-4.5" /> Back to FPO Connect Directory
          </button>
        </div>

        {/* ── Hero Profile Card ── */}
        <div className="bg-white border border-gray-200 rounded-sm shadow-xs overflow-hidden">
          {/* Green header bar */}
          <div className="bg-[#0f9648] px-6 py-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-sm bg-white/20 flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="text-green-100 text-[10px] font-bold uppercase tracking-wider block">
                Farmer Producer Organization
              </span>
              <h1 className="text-white font-black text-lg sm:text-xl tracking-tight leading-tight">
                {fpoName}
              </h1>
            </div>
          </div>

          {/* Info grid */}
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {/* Contact Number */}
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-sm bg-green-50 border border-green-100 flex items-center justify-center shrink-0">
                <Phone className="w-4 h-4 text-[#0f9648]" />
              </div>
              <div className="space-y-0.5 min-w-0">
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">
                  FPO Contact Number
                </span>
                {registeredPhone ? (
                  <a
                    href={`tel:${registeredPhone}`}
                    className="text-gray-900 font-bold text-sm hover:text-[#0f9648] transition-colors"
                  >
                    {registeredPhone}
                  </a>
                ) : (
                  <span className="text-gray-400 font-medium text-sm">—</span>
                )}
              </div>
            </div>

            {/* GST Number */}
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-sm bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
                <FileText className="w-4 h-4 text-blue-600" />
              </div>
              <div className="space-y-0.5 min-w-0">
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">
                  GST Number
                </span>
                <span className="text-gray-900 font-bold text-sm uppercase">
                  {gstNumber || "Not Provided"}
                </span>
              </div>
            </div>

            {/* Address */}
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-sm bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-red-500" />
              </div>
              <div className="space-y-0.5 min-w-0">
                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">
                  Registered Address
                </span>
                <span className="text-gray-800 font-semibold text-sm leading-normal">
                  {[registeredAddress, registeredTalukName, registeredDistrictName]
                    .filter(Boolean)
                    .join(", ") || "Karnataka, India"}
                </span>
              </div>
            </div>

            {/* Categories */}
            {allCategories.length > 0 && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-sm bg-purple-50 border border-purple-100 flex items-center justify-center shrink-0">
                  <Layers className="w-4 h-4 text-purple-600" />
                </div>
                <div className="space-y-1 min-w-0">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">
                    Stock Categories
                  </span>
                  <div className="flex flex-wrap gap-1">
                    {allCategories.map((cat) => (
                      <span
                        key={cat}
                        className="inline-flex items-center bg-green-50 text-[#0f9648] text-[10px] font-black px-2 py-0.5 rounded-sm border border-green-200"
                      >
                        {cat}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Activities */}
            {allActivities.length > 0 && (
              <div className="flex items-start gap-3 sm:col-span-2">
                <div className="w-8 h-8 rounded-sm bg-amber-50 border border-amber-100 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-4 h-4 text-amber-600" />
                </div>
                <div className="space-y-1 min-w-0">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider block">
                    Activities Carried Out
                  </span>
                  <div className="flex flex-wrap gap-x-6 gap-y-1">
                    {allActivities.map((act, i) => (
                      <span key={i} className="text-gray-700 font-semibold text-xs">
                        {i + 1}. {act}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Market Listings Section ── */}
        <div className="space-y-4">
          {/* Section header + category filter */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <h2 className="text-sm font-black text-gray-800 uppercase tracking-wider">
              Market Listings &nbsp;
              <span className="text-[#0f9648] font-black">({filteredStocks.length})</span>
            </h2>

            {uniqueCategories.length > 1 && (
              <div className="relative min-w-[180px]">
                <label htmlFor="detail-category-filter" className="sr-only">Filter by Category</label>
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5" />
                <select
                  id="detail-category-filter"
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full pl-8 pr-8 py-2 bg-white border border-gray-200 rounded-sm text-xs text-gray-700 font-semibold focus:ring-1 focus:ring-green-500 focus:border-green-550 outline-hidden transition cursor-pointer appearance-none"
                >
                  <option value="">All Categories</option>
                  {uniqueCategories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Listings */}
          {filteredStocks.length > 0 ? (
            filteredStocks.map((stockEntry) => (
              <div
                key={stockEntry._id}
                className="bg-[#dcf7e3] border border-[#a5d6a7] rounded-xs p-6 sm:p-8 space-y-6 text-gray-900 font-sans shadow-xs"
              >
                {/* Header row: category + contact */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <h3 className="inline-flex items-center bg-white text-[#0f9648] text-xs font-black px-2.5 py-1 rounded-sm border border-green-200 uppercase tracking-wider">
                    Category: {stockEntry.category?.name || "Uncategorized"}
                  </h3>
                  <div className="flex flex-col sm:flex-row gap-4 text-sm">
                    {stockEntry.contactPerson && (
                      <div>
                        <span className="font-extrabold text-gray-855">Contact Person: </span>
                        <span className="text-gray-800">{stockEntry.contactPerson}</span>
                      </div>
                    )}
                    {stockEntry.mobileNumber && (
                      <div>
                        <span className="font-extrabold text-gray-855">Mob: </span>
                        <a
                          href={`tel:${stockEntry.mobileNumber}`}
                          className="text-[#0f9648] font-bold hover:underline"
                        >
                          {stockEntry.mobileNumber}
                        </a>
                      </div>
                    )}
                    {stockEntry.scheme && stockEntry.scheme !== "None" && (
                      <div>
                        <span className="font-extrabold text-gray-855">Scheme: </span>
                        <span className="text-gray-800">{stockEntry.scheme}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Output Supply Table */}
                <div className="space-y-3">
                  <h4 className="font-extrabold text-xs text-gray-905">
                    Output Supply — Crop Details:
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-[12px] border-collapse bg-white border border-gray-400">
                      <thead>
                        <tr className="bg-[#e2f7e7] text-gray-800 font-black border-b border-gray-400">
                          <th className="py-2 px-3 border border-gray-400 w-20 text-center">Sl. No.</th>
                          <th className="py-2 px-3 border border-gray-400 w-1/4">Crop Name</th>
                          <th className="py-2 px-3 border border-gray-400 w-1/4">Quantity</th>
                          <th className="py-2 px-3 border border-gray-400 w-1/4">Price Range (Rs.)</th>
                          <th className="py-2 px-3 border border-gray-400">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockEntry.outputCrops && stockEntry.outputCrops.length > 0 ? (
                          stockEntry.outputCrops.map((crop, index) => (
                            <tr key={index} className="border-b border-gray-450">
                              <td className="py-2 px-3 border border-gray-400 text-center font-bold text-gray-700">
                                {index + 1}
                              </td>
                              <td className="py-2 px-3 border border-gray-400 text-gray-800 font-medium">
                                {crop.cropName}
                              </td>
                              <td className="py-2 px-3 border border-gray-400 text-gray-800 font-medium">
                                {crop.quantity}
                              </td>
                              <td className="py-2 px-3 border border-gray-400 text-gray-800 font-medium">
                                {crop.priceRange || ""}
                              </td>
                              <td className="py-2 px-3 border border-gray-400 text-gray-850 font-medium">
                                {crop.remarks || ""}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="py-4 px-3 text-center text-gray-500 italic font-medium">
                              No output crops registered
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Input Requirements Table */}
                <div className="space-y-3 pt-4 border-t border-green-200/50">
                  <h4 className="font-extrabold text-xs text-gray-905">
                    Input Requirement — Product Details:
                  </h4>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-[12px] border-collapse bg-white border border-gray-400">
                      <thead>
                        <tr className="bg-[#e2f7e7] text-gray-800 font-black border-b border-gray-400">
                          <th className="py-2 px-3 border border-gray-400 w-20 text-center">Sl. No.</th>
                          <th className="py-2 px-3 border border-gray-400 w-1/4">Product Name</th>
                          <th className="py-2 px-3 border border-gray-400 w-1/4">Qty/No.</th>
                          <th className="py-2 px-3 border border-gray-400 w-1/4">Value (Rs.)</th>
                          <th className="py-2 px-3 border border-gray-400">Remarks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockEntry.inputRequirements && stockEntry.inputRequirements.length > 0 ? (
                          stockEntry.inputRequirements.map((req, index) => (
                            <tr key={index} className="border-b border-gray-450">
                              <td className="py-2 px-3 border border-gray-400 text-center font-bold text-gray-700">
                                {index + 1}
                              </td>
                              <td className="py-2 px-3 border border-gray-400 text-gray-800 font-medium">
                                {req.productName}
                              </td>
                              <td className="py-2 px-3 border border-gray-400 text-gray-800 font-medium">
                                {req.qty}
                              </td>
                              <td className="py-2 px-3 border border-gray-400 text-gray-800 font-medium">
                                {req.value || ""}
                              </td>
                              <td className="py-2 px-3 border border-gray-400 text-gray-850 font-medium">
                                {req.remarks || ""}
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="py-4 px-3 text-center text-gray-500 italic font-medium">
                              No input requirements registered
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="min-h-[220px] flex flex-col items-center justify-center p-8 bg-white rounded-xs border border-gray-200 shadow-2xs text-center">
              <AlertCircle className="w-12 h-12 text-[#0f9648] mb-3 opacity-60" />
              <h4 className="font-bold text-gray-800 text-sm mb-1">No matching listings found</h4>
              <p className="text-xs text-gray-500 max-w-sm mb-4">
                No listings match the selected category filter.
              </p>
              <button
                onClick={() => setSelectedCategory("")}
                className="bg-green-600 hover:bg-green-700 text-white font-bold px-4 py-2 rounded-sm text-xs transition cursor-pointer"
              >
                Reset Filter
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
